// Player main entry — offline-first dengan IndexedDB cache.
//
// Flow:
//   1. Cek device_token; kalau gak ada -> redirect /pair.html.
//   2. Request persistent storage (supaya browser tidak auto-evict cache).
//   3. Load manifest terakhir dari IndexedDB (kalau ada) -> render dari cache.
//      Player tetap jalan meskipun WiFi mati / server down.
//   4. Coba sync ke server (kalau online):
//      - GET /devices/manifest -> cek apakah ada manifest aktif terbaru.
//      - Connect Socket.IO -> listen push:content event.
//   5. Saat manifest tiba (initial atau via socket):
//      - Tampilkan progress prefetch.
//      - Download media yang belum di-cache.
//      - Render dari cache.
//      - Evict media yang tidak terpakai > 7 hari.
//   6. Heartbeat ke server tiap 60 detik (best-effort).

import { tokenManager } from "./core/token-manager.js";
import {
  attachRenderer,
  setManifest,
  showLoading,
  showLoadingProgress,
  captureScreenshot,
  type Manifest,
} from "./core/renderer.js";
import { connectSocket } from "./core/socket.js";
import { apiFetch } from "./core/api-client.js";
import {
  requestPersistentStorage,
  getStorageEstimate,
  evictUnused,
  setMeta,
  getMeta,
  deleteCachedMedia,
} from "./core/media-cache.js";
import { prefetchMedia } from "./core/prefetcher.js";
import { extractMediaList, withLanRewrite } from "./core/manifest-utils.js";
import { collectTelemetry } from "./core/telemetry.js";
import { detectDevice } from "./core/device.js";

const LAST_MANIFEST_KEY = "last_manifest";

const root = document.getElementById("player-root");
if (!root) throw new Error("Missing #player-root in index.html");

const payload = tokenManager.get();
if (!payload) {
  window.location.replace("/pair.html");
  // eslint-disable-next-line @typescript-eslint/no-throw-literal
  throw new Error("redirecting");
}

attachRenderer(root);
showLoading("Memuat...");

// =========================================================================
// Orientation — apply rotation berdasarkan setting di dashboard.
// Fetch /devices/info on boot, listen socket screen:update untuk live change.
// =========================================================================

type Orientation = "landscape" | "portrait" | "flipped_landscape" | "flipped_portrait";

// =========================================================================
// Working Hours enforcement — layar auto blackout di luar jam operasional.
// =========================================================================

interface PowerSchedule {
  enabled: boolean;
  on: string; // "HH:MM"
  off: string; // "HH:MM"
  days: string[]; // ["mon","tue",...]
}

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
let powerScheduleTimer: ReturnType<typeof setInterval> | null = null;
let currentPowerSchedule: PowerSchedule | null = null;

function minutesFromHHMM(s: string): number {
  const [h, m] = s.split(":").map((x) => parseInt(x, 10));
  return (h || 0) * 60 + (m || 0);
}

/** Apakah layar HARUS mati sekarang menurut jadwal (evaluasi waktu lokal player). */
function isPoweredOff(ps: PowerSchedule, now: Date): boolean {
  if (!ps.enabled) return false;
  const dayKey = DAY_KEYS[now.getDay()] ?? "sun";
  if (ps.days.length && !ps.days.includes(dayKey)) return false; // hari non-aktif → biarkan nyala
  const cur = now.getHours() * 60 + now.getMinutes();
  const on = minutesFromHHMM(ps.on);
  const off = minutesFromHHMM(ps.off);
  if (on === off) return false;
  // Rentang normal (nyala 07:00–23:00): mati kalau di luar [on, off).
  if (on < off) return !(cur >= on && cur < off);
  // Rentang overnight (nyala 22:00–06:00): mati kalau di dalam [off, on).
  return cur >= off && cur < on;
}

function ensureBlackoutEl(): HTMLElement {
  let el = document.getElementById("power-blackout");
  if (!el) {
    el = document.createElement("div");
    el.id = "power-blackout";
    el.style.cssText =
      "position:fixed;inset:0;background:#000;z-index:2147483646;display:none;" +
      "align-items:center;justify-content:center;color:#1a1a1a;font-family:system-ui,sans-serif;font-size:12px;";
    el.textContent = "";
    document.body.appendChild(el);
  }
  return el;
}

function evaluatePowerSchedule() {
  const ps = currentPowerSchedule;
  const el = ensureBlackoutEl();
  if (!ps || !ps.enabled) {
    el.style.display = "none";
    return;
  }
  el.style.display = isPoweredOff(ps, new Date()) ? "flex" : "none";
}

function applyPowerSchedule(ps: PowerSchedule | null) {
  currentPowerSchedule = ps && typeof ps.enabled === "boolean" ? ps : null;
  console.log("[power] schedule:", currentPowerSchedule);
  evaluatePowerSchedule();
  if (!powerScheduleTimer) {
    // Cek tiap 30 dtk supaya transisi on/off cukup presisi.
    powerScheduleTimer = setInterval(evaluatePowerSchedule, 30_000);
  }
}

// =========================================================================
// Media filtering — buang item playlist dengan tipe media yang diblokir.
// =========================================================================

interface MediaFilter {
  enabled?: boolean;
  blockedTypes?: string[]; // "image" | "video" | "audio" | "document" | "url"
}

let currentMediaFilter: MediaFilter | null = null;

function applyMediaFilter(mf: MediaFilter | null) {
  currentMediaFilter = mf && mf.enabled && mf.blockedTypes?.length ? mf : null;
  console.log("[filter] media:", currentMediaFilter);
}

/** Salin manifest dengan item playlist bertipe terblokir dibuang. Non-mutating. */
function filterManifest(m: Manifest | null): Manifest | null {
  if (!m || !currentMediaFilter) return m;
  const blocked = new Set(currentMediaFilter.blockedTypes ?? []);
  if (!blocked.size) return m;
  // Single media terblokir → jadikan idle (null) supaya tidak tampil.
  if (m.type === "media") {
    return blocked.has(m.media.media_type) ? null : m;
  }
  if (m.type === "playlist") {
    const items = m.playlist.items.filter((it) => !it.media || !blocked.has(it.media.media_type));
    return { ...m, playlist: { ...m.playlist, items } };
  }
  return m;
}

// =========================================================================
// Interactivity — kunci/buka sentuh (mode kiosk) + sembunyikan kursor.
// =========================================================================

interface Interactivity {
  touchEnabled?: boolean;
  hideCursor?: boolean;
}

function applyInteractivity(cfg: Interactivity | null) {
  const dev = detectDevice();
  // Default adaptif per-device kalau admin belum set eksplisit:
  //  - TV / desktop-kiosk → sentuh terkunci (lean-back, dikontrol remote)
  //  - tablet / phone → sentuh aktif (memang buat disentuh)
  const deviceTouchDefault = dev.deviceClass === "tablet" || dev.deviceClass === "phone";
  const touchOn = cfg?.touchEnabled ?? deviceTouchDefault;
  document.documentElement.style.setProperty("--player-pointer", touchOn ? "auto" : "none");
  document.body.style.pointerEvents = touchOn ? "auto" : "none";
  // Kursor: sembunyikan kalau diminta; default sembunyi di TV/kiosk, tampil di HP/tablet.
  const hide = cfg?.hideCursor ?? (dev.isTV || !touchOn);
  document.body.style.cursor = hide ? "none" : "auto";
  document.documentElement.setAttribute("data-device", dev.deviceClass);
  console.log("[touch] device:", dev.deviceClass, "enabled:", touchOn, "hideCursor:", hide);
}

// =========================================================================
// Emergency alert — override darurat fullscreen, DI ATAS semua (termasuk
// blackout Working Hours), supaya pesan darurat selalu terlihat.
// =========================================================================

interface EmergencyAlert {
  active?: boolean;
  message?: string;
  level?: "info" | "warning" | "danger";
  title?: string;
}

const ALERT_COLORS: Record<string, { bg: string; fg: string }> = {
  info: { bg: "#1d4ed8", fg: "#ffffff" },
  warning: { bg: "#b45309", fg: "#ffffff" },
  danger: { bg: "#b91c1c", fg: "#ffffff" },
};

function ensureAlertEl(): HTMLElement {
  let el = document.getElementById("emergency-alert");
  if (!el) {
    el = document.createElement("div");
    el.id = "emergency-alert";
    el.style.cssText =
      "position:fixed;inset:0;z-index:2147483647;display:none;" +
      "flex-direction:column;align-items:center;justify-content:center;gap:2vh;" +
      "padding:4vw;text-align:center;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;";
    document.body.appendChild(el);
  }
  return el;
}

function applyAlert(alert: EmergencyAlert | null) {
  const el = ensureAlertEl();
  if (!alert || !alert.active || !alert.message) {
    el.style.display = "none";
    el.innerHTML = "";
    return;
  }
  const c = ALERT_COLORS[alert.level ?? "danger"] ?? ALERT_COLORS.danger!;
  el.style.background = c.bg;
  el.style.color = c.fg;
  const title = alert.title || "PERINGATAN DARURAT";
  // textContent lewat node terpisah supaya pesan tidak di-render sebagai HTML.
  el.innerHTML =
    '<div style="font-size:3vw;font-weight:800;letter-spacing:.15em;opacity:.9"></div>' +
    '<div style="font-size:6vw;font-weight:800;line-height:1.2;max-width:90vw"></div>';
  const [tEl, mEl] = [el.children[0] as HTMLElement, el.children[1] as HTMLElement];
  tEl.textContent = "⚠ " + title;
  mEl.textContent = alert.message;
  el.style.display = "flex";
  console.log("[alert] active:", alert.level, alert.message);
}

function applyOrientation(orientation: string | null | undefined) {
  const valid: Orientation[] = ["landscape", "portrait", "flipped_landscape", "flipped_portrait"];
  const o = (valid as string[]).includes(orientation ?? "")
    ? (orientation as Orientation)
    : "landscape";
  document.documentElement.setAttribute("data-orientation", o);
}

async function fetchAndApplyDeviceInfo() {
  try {
    const res = await apiFetch<{
      data: {
        orientation: string | null;
        name: string;
        power_schedule?: PowerSchedule | null;
        emergency_alert?: EmergencyAlert | null;
        media_filter?: MediaFilter | null;
        interactivity?: Interactivity | null;
      };
    }>("/devices/info", { deviceToken: payload!.device_token });
    applyOrientation(res.data.orientation);
    applyPowerSchedule(res.data.power_schedule ?? null);
    applyAlert(res.data.emergency_alert ?? null);
    applyMediaFilter(res.data.media_filter ?? null);
    applyInteractivity(res.data.interactivity ?? null);
  } catch (err) {
    if (err instanceof Error && /\b401\b|DEVICE_TOKEN_INVALID|UNAUTHORIZED/.test(err.message)) {
      tokenManager.clear();
      window.location.replace("/pair.html");
      return;
    }
    // Fallback: landscape (default).
    applyOrientation("landscape");
  }
}

// Apply landscape default segera supaya layout gak flicker, lalu fetch real value.
applyOrientation("landscape");
void fetchAndApplyDeviceInfo();

// =========================================================================
// Boot: persistent storage + load last manifest dari cache
// =========================================================================

void (async () => {
  // Minta browser jangan auto-evict (best-effort, kadang gak granted di mobile).
  const persisted = await requestPersistentStorage();
  console.log("[cache] persistent storage:", persisted);
  const est = await getStorageEstimate();
  if (est) {
    console.log(
      `[cache] quota: ${(est.quota / 1e6).toFixed(0)} MB, usage: ${(est.usage / 1e6).toFixed(0)} MB (${est.usagePct.toFixed(1)}%)`,
    );
  }

  // Step 1: render dari manifest cached (offline-first).
  const cachedManifest = await getMeta<Manifest>(LAST_MANIFEST_KEY);
  if (cachedManifest) {
    console.log("[cache] applying last manifest from IDB");
    setManifest(filterManifest(cachedManifest));
  } else {
    showLoading("Menunggu konten");
  }

  // Step 2: sync ke server (kalau online). Kalau gagal, fallback ke cache.
  void syncFromServer();
})();

// =========================================================================
// Sync flow: ambil manifest terbaru + prefetch
// =========================================================================

async function applyManifest(manifest: Manifest, opts: { showProgress: boolean }) {
  // Prefetch semua media ke IDB. Skip yang sudah ada.
  const items = withLanRewrite(extractMediaList(manifest));
  if (items.length === 0) {
    setManifest(manifest);
    return;
  }

  let lastProgress = 0;
  const result = await prefetchMedia(items, (p) => {
    if (!opts.showProgress) return;
    if (p.completed === lastProgress) return;
    lastProgress = p.completed;
    showLoadingProgress(p.completed, p.total, p.currentName);
  });

  console.log("[cache] prefetch done:", result);
  await setMeta(LAST_MANIFEST_KEY, manifest);

  // Eviction: hapus media lama yang tidak ada di manifest aktif.
  const keepIds = new Set(items.map((i) => i.id));
  void evictUnused({ keepIds }).then((r) => {
    if (r.evicted.length > 0) {
      console.log(
        `[cache] evicted ${r.evicted.length} media, freed ${(r.bytesFreed / 1e6).toFixed(1)} MB`,
      );
    }
  });

  setManifest(manifest);
}

async function syncFromServer() {
  try {
    const res = await apiFetch<{ data: Manifest | null }>("/devices/manifest", {
      deviceToken: payload!.device_token,
    });
    if (res.data) {
      const last = await getMeta<Manifest>(LAST_MANIFEST_KEY);
      const isNewer = !last || last.push_id !== res.data.push_id;
      const showProgress = !last;
      if (isNewer) {
        await applyManifest(filterManifest(res.data) ?? res.data, { showProgress });
      }
    }
  } catch (err) {
    console.warn("[sync] gagal sync manifest dari server:", err);
    if (err instanceof Error && err.message.includes("401")) {
      tokenManager.clear();
      window.location.replace("/pair.html");
    }
    // Kalau gagal sync tapi sudah ada manifest cached, biarkan player lanjut play offline.
  }
}

// =========================================================================
// Socket.IO untuk real-time push
// =========================================================================

const socket = connectSocket(payload!.device_token);

socket.on("connect", () => {
  console.log("[socket] connected", socket.id);
});

socket.on("disconnect", (reason) => {
  console.log("[socket] disconnected:", reason);
});

socket.on("connect_error", (err) => {
  console.error("[socket] connect error:", err.message);
  if (err.message.includes("device_token")) {
    tokenManager.clear();
    window.location.replace("/pair.html");
  }
});

// Screen di-delete admin dari dashboard -> reset token + kembali ke pair screen.
socket.on("screen:deleted", () => {
  console.warn("[socket] screen deleted by admin, resetting...");
  tokenManager.clear();
  window.location.replace("/pair.html");
});

// Screen meta updated (orientation, name, dll.) -> apply tanpa reload.
socket.on(
  "screen:update",
  async (payload: {
    orientation?: string;
    name?: string;
    power_schedule?: PowerSchedule | null;
    media_filter?: MediaFilter | null;
    interactivity?: Interactivity | null;
  }) => {
    console.log("[socket] screen:update", payload);
    if (payload.orientation) applyOrientation(payload.orientation);
    if (payload.power_schedule !== undefined) applyPowerSchedule(payload.power_schedule ?? null);
    if (payload.interactivity !== undefined) applyInteractivity(payload.interactivity ?? null);
    if (payload.media_filter !== undefined) {
      applyMediaFilter(payload.media_filter ?? null);
      // Terapkan ulang ke konten yang sedang tampil.
      const cur = await getMeta<Manifest>(LAST_MANIFEST_KEY);
      if (cur) setManifest(filterManifest(cur));
    }
  },
);

// Emergency alert dari dashboard (tab Emergency Alerts) — override fullscreen.
socket.on("screen:alert", (alert: EmergencyAlert | null) => {
  console.log("[socket] screen:alert", alert);
  applyAlert(alert && alert.active ? alert : null);
});

// Remote command dari dashboard (tab Advanced): reload / resync / clear cache.
socket.on("screen:command", async (payload: { action?: string }) => {
  console.log("[socket] screen:command", payload);
  switch (payload.action) {
    case "reload":
      window.location.reload();
      break;
    case "resync":
      await syncFromServer();
      break;
    case "clear_cache":
      try {
        await setMeta(LAST_MANIFEST_KEY, null);
        await evictUnused({ keepIds: new Set() }); // kosongkan semua media cache
      } catch (err) {
        console.warn("[cmd] clear_cache gagal:", err);
      }
      window.location.reload();
      break;
    default:
      console.warn("[cmd] unknown action:", payload.action);
  }
});

// Admin unassign konten -> player kembali ke idle (placeholder "Menunggu konten").
socket.on("push:clear", async () => {
  console.log("[socket] push:clear, kembali ke idle");
  await setMeta(LAST_MANIFEST_KEY, null);
  setManifest(null);
});

socket.on("push:content", async (raw: Manifest) => {
  console.log("[socket] received push:content", raw);
  socket.emit("push:ack", { push_id: raw.push_id, status: "received" });
  const manifest = filterManifest(raw) ?? raw; // buang tipe terblokir; kalau semua kebuang, pakai raw
  const last = await getMeta<Manifest>(LAST_MANIFEST_KEY);
  if (last && last.push_id === raw.push_id) {
    console.log("[push] same push_id sebagai cached, skip prefetch");
    setManifest(manifest);
    // Konten sudah tampil (dari cache) → konfirmasi rendered ke server.
    socket.emit("push:ack", { push_id: raw.push_id, status: "rendered" });
    return;
  }
  try {
    await applyManifest(manifest, { showProgress: true });
    // Berhasil render → konfirmasi 'rendered' supaya server tahu push sukses.
    socket.emit("push:ack", { push_id: manifest.push_id, status: "rendered" });
  } catch (err) {
    console.error("[push] gagal apply manifest:", err);
  }
});

// Instant cache invalidation: admin hapus media di dashboard -> backend broadcast
// event ini ke semua player di workspace. Hapus blob dari IndexedDB cache supaya
// player tidak pakai versi lama. Kalau media masih di-reference manifest yang
// sedang main, force re-fetch manifest dari server (yang sudah tidak include
// media tsb) supaya rendering bersih.
socket.on(
  "media:deleted",
  async (payload: { media_id: string; workspace_id?: string }) => {
    console.log("[socket] media:deleted", payload.media_id);
    try {
      await deleteCachedMedia(payload.media_id);
    } catch (err) {
      console.warn("[cache] failed to evict deleted media:", err);
    }

    // Kalau manifest aktif masih reference media ini, sync ulang dari server
    // supaya tidak nge-render thumbnail/file yang sudah hilang.
    const last = await getMeta<Manifest>(LAST_MANIFEST_KEY);
    const stillReferenced = last
      ? extractMediaList(last).some((m) => m.id === payload.media_id)
      : false;
    if (stillReferenced) {
      console.log("[cache] deleted media masih di manifest aktif, re-sync");
      await syncFromServer();
    }
  },
);

// =========================================================================
// Telemetry — kirim spek hardware (RAM, storage, uptime, dll.) ke server.
// Sekaligus update last_seen_at + status=online (gantiin heartbeat).
// Frekuensi: langsung saat boot + tiap 60 detik berikutnya.
// =========================================================================

async function sendTelemetry() {
  try {
    const data = await collectTelemetry();
    await apiFetch("/devices/telemetry", {
      method: "POST",
      deviceToken: payload!.device_token,
      body: JSON.stringify(data),
      headers: { "Content-Type": "application/json" },
    });
  } catch (err) {
    // 401 berarti screen sudah di-delete admin atau token invalid -> reset & pair ulang.
    if (err instanceof Error && /\b401\b|DEVICE_TOKEN_INVALID|UNAUTHORIZED/.test(err.message)) {
      console.warn("[telemetry] device token invalid, kembali ke pair screen");
      tokenManager.clear();
      window.location.replace("/pair.html");
      return;
    }
    // best-effort, ignore network errors
  }
}

// Boot — kirim segera supaya dashboard punya data fresh.
void sendTelemetry();
setInterval(() => void sendTelemetry(), 60_000);

// =========================================================================
// Screenshot — capture konten yang sedang tampil + upload ke server.
// Frekuensi: 30 detik setelah boot (delay supaya konten sudah render dulu)
// kemudian tiap 5 menit. Best-effort, ignore error.
// =========================================================================

async function sendScreenshot() {
  try {
    const dataUrl = await captureScreenshot();
    if (!dataUrl) return; // gagal capture (mis. iframe + no placeholder)
    await apiFetch("/devices/screenshot", {
      method: "POST",
      deviceToken: payload!.device_token,
      body: JSON.stringify({ image_base64: dataUrl }),
      headers: { "Content-Type": "application/json" },
    });
  } catch (err) {
    // 401: token revoked karena screen di-delete.
    if (err instanceof Error && /\b401\b|DEVICE_TOKEN_INVALID|UNAUTHORIZED/.test(err.message)) {
      tokenManager.clear();
      window.location.replace("/pair.html");
      return;
    }
    console.warn("[screenshot] upload gagal:", err);
  }
}

// First capture 30 detik setelah boot (kasih waktu render pertama).
setTimeout(() => {
  void sendScreenshot();
  setInterval(() => void sendScreenshot(), 5 * 60_000);
}, 30_000);
